import { PlusIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import BrandingBadges from 'components/layout/branding-badges';
import {
  BreadcrumbLink,
  Separator
} from 'components/layout/breadcrumb-elements';
import StructureActionsMenu from 'components/layout/structure-actions-menu';
import StructureSwitcher from 'components/layout/structure-switcher';
import Tooltip from 'components/layout/tooltip';
import {
  setStructureNameByUser,
  setStructureOptions
} from 'features/structure';
import { useState } from 'react';
import axios from 'utils/api';
import { refetchStructure } from 'utils/data';
import {
  buildStructureSelectOptions,
  getStructureDisplayName,
  handleStructureChange
} from 'utils/structure';
import { duplicateStructure } from 'utils/structure-duplicate';
import { confirmationModal, notification } from 'utils/ui';

function titleCase(str: string | null | undefined): string | null {
  if (!str) return null;
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function BreadcrumbBar() {
  const dispatch = useAppDispatch();
  const { projectDetails, attomData } = useAppSelector(
    (state) => state.project
  );
  const { structureTypes } = useAppSelector((state) => state.data);
  const {
    structureUniqueId,
    structureOptions,
    structureTypeId,
    structureSubtypeId,
    structureName,
    structureActive
  } = useAppSelector((state) => state.structure);

  const [renamingStructure, setRenamingStructure] = useState(false);
  const [structureNameInput, setStructureNameInput] = useState('');
  const [reactivating, setReactivating] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const displayName = getStructureDisplayName({
    structureName,
    structureTypeId,
    structureSubtypeId,
    structureTypes
  });

  const structureSelectOptions = buildStructureSelectOptions({
    structureOptions,
    structureUniqueId,
    displayName,
    includeNewOption: false
  });

  const address = attomData?.address;
  const formattedAddress = address
    ? [
        titleCase(address.line1),
        titleCase(address.locality),
        address.countrySubd
      ]
        .filter(Boolean)
        .join(', ')
    : null;

  const customerName = projectDetails?.customerName;
  const customerId = projectDetails?.customerId;
  const projectId = projectDetails?.projectId;
  const addressId = useAppSelector((state) => state.project.addressId);
  const projectName =
    projectDetails?.jobName ||
    (projectDetails ? `Project #${projectId}` : null);

  const partnernetBase = `${process.env.REACT_APP_PARTNERNET_URL}/partnernet`;
  const customerHref =
    partnernetBase && customerId
      ? `${partnernetBase}/customer/${customerId}`
      : undefined;
  const addressHref =
    partnernetBase && addressId
      ? `${partnernetBase}/address/${addressId}`
      : undefined;
  const projectHref =
    partnernetBase && projectId
      ? `${partnernetBase}/project/${projectId}`
      : undefined;

  const handleDeactivateStructure = (uniqueId: string, label: string) => {
    confirmationModal(
      `Are you sure you want to delete "${label}"?`,
      async () => {
        await axios.delete('structure/deactivate', {
          data: { uniqueId }
        });
        dispatch(
          setStructureOptions(
            structureOptions.filter((opt) => opt.uniqueId !== uniqueId)
          )
        );
      },
      'Delete Structure'
    );
  };

  const handleReactivateStructure = async () => {
    if (!structureUniqueId) return;
    setReactivating(true);
    try {
      await axios.put('structure/reactivate', { uniqueId: structureUniqueId });
      await refetchStructure(structureUniqueId);
    } finally {
      setReactivating(false);
    }
  };

  const handleRenameClick = () => {
    setStructureNameInput(displayName ?? '');
    setRenamingStructure(true);
  };

  const handleRenameCommit = () => {
    if (structureNameInput.trim()) {
      dispatch(setStructureNameByUser(structureNameInput.trim()));
    }
    setRenamingStructure(false);
  };

  const handleRenameKeyDown = (key: string) => {
    if (key === 'Enter') {
      handleRenameCommit();
    } else if (key === 'Escape') {
      setRenamingStructure(false);
    }
  };

  const handleDuplicateStructure = async () => {
    if (!structureUniqueId || !projectId || duplicating) return;
    setDuplicating(true);
    try {
      const newStructureUniqueId = await duplicateStructure(
        projectId,
        structureUniqueId
      );
      handleStructureChange(newStructureUniqueId);
    } catch (error) {
      console.error('Failed to duplicate structure:', error);
      notification('Failed to duplicate structure', 'error');
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <>
      <div className='flex h-8 w-full items-center gap-1 bg-neutral-900 px-4 text-xs text-white'>
        <BreadcrumbLink label={customerName} href={customerHref} />
        <Separator />
        <BreadcrumbLink label={formattedAddress} href={addressHref} />
        <Separator />
        <BreadcrumbLink label={projectName} href={projectHref} />
        <Separator />
        <div className='group flex items-center gap-1'>
          {renamingStructure ? (
            <input
              className='w-40 rounded border border-neutral-600 bg-neutral-800 px-1.5 py-0.5 text-xs text-white outline-none focus:border-neutral-400'
              value={structureNameInput}
              onChange={(e) => setStructureNameInput(e.target.value)}
              onKeyDown={(e) => handleRenameKeyDown(e.key)}
              onBlur={handleRenameCommit}
              autoFocus
            />
          ) : (
            <StructureSwitcher
              label={displayName ?? 'Select structure...'}
              options={structureSelectOptions}
              selectedUniqueId={structureUniqueId}
              onSelect={handleStructureChange}
              onDeactivate={handleDeactivateStructure}
            />
          )}
          <StructureActionsMenu
            disabled={!structureUniqueId}
            duplicating={duplicating}
            onRename={handleRenameClick}
            onDuplicate={handleDuplicateStructure}
          />
          <button
            type='button'
            aria-label='New Structure'
            className='relative cursor-pointer text-neutral-400 hover:text-white'
            onClick={() => handleStructureChange('new')}
          >
            <PlusIcon className='h-4 w-4' />
            <Tooltip text='New Structure' />
          </button>
        </div>
        <BrandingBadges
          trailingBadge={
            <span className='rounded-full bg-blue-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-300'>
              Beta
            </span>
          }
        />
      </div>
      {structureActive === false && (
        <div className='flex w-full items-center gap-2 bg-amber-600 px-4 py-1 text-xs text-white'>
          <span>This structure has been de-activated.</span>
          <button
            type='button'
            disabled={reactivating}
            onClick={handleReactivateStructure}
            className='rounded border border-white/70 px-2 py-0.5 hover:bg-white/10 disabled:opacity-50'
          >
            {reactivating ? 'Re-activating...' : 'Re-activate'}
          </button>
        </div>
      )}
    </>
  );
}
